import Phaser from "phaser";
import type { EventBus } from "./EventBus";
import { resolveAssetUrl } from "./assetRoot";

const CHANNEL_NAMES = ["bgm", "ambient", "voice", "sfx"] as const;
const BGM_CROSSFADE_MS = 1000;

type PlayableSound = Phaser.Sound.BaseSound & {
  volume: number;
  seek: number;
  loop: boolean;
};

interface ChannelState {
  sound: PlayableSound | null;
  asset: string | null;
  volume: number;
  loop: boolean;
}

export interface AudioSnapshot {
  channels: Record<string, {
    asset: string | null;
    volume: number;
    loop: boolean;
    position: number;
  }>;
  masterVolume: number;
}

export class AudioManager {
  private scene: Phaser.Scene;
  private unsubs: Array<() => void> = [];
  private channels = new Map<string, ChannelState>();
  private _masterVolume = 1.0;
  private pendingCommands: Array<() => void> = [];
  private unlocked = false;
  private loadQueue = new Map<string, Array<() => void>>();

  constructor(scene: Phaser.Scene, bus: EventBus) {
    this.scene = scene;

    for (const ch of CHANNEL_NAMES) {
      this.channels.set(ch, { sound: null, asset: null, volume: 1.0, loop: false });
    }

    this.unsubs.push(
      bus.on("audio.play", (d) => this._play(d.channel, d.asset, d.loop, d.fade_in)),
      bus.on("audio.stop", (d) => this._stop(d.channel, d.fade_out)),
    );

    if (scene.sound.locked) {
      scene.sound.once("unlocked", () => {
        this.unlocked = true;
        this._flushPending();
      });
    } else {
      this.unlocked = true;
    }
  }

  private _flushPending() {
    const cmds = this.pendingCommands;
    this.pendingCommands = [];
    for (const cmd of cmds) cmd();
  }

  private _ensureLoaded(key: string, cb: () => void) {
    if (this.scene.cache.audio.exists(key)) {
      cb();
      return;
    }

    if (this.loadQueue.has(key)) {
      this.loadQueue.get(key)!.push(cb);
      return;
    }

    this.loadQueue.set(key, [cb]);
    const hasExtension = key.split("/").pop()?.includes(".");
    const url = hasExtension ? key : `${key}.mp3`;
    this.scene.load.audio(key, resolveAssetUrl(url));
    this.scene.load.once(`filecomplete-audio-${key}`, () => {
      const cbs = this.loadQueue.get(key) ?? [];
      this.loadQueue.delete(key);
      for (const fn of cbs) fn();
    });
    this.scene.load.once("loaderror", (file: Phaser.Loader.File) => {
      if (file.key === key) {
        console.warn(`AudioManager: failed to load "${key}"`);
        this.loadQueue.delete(key);
      }
    });
    this.scene.load.start();
  }

  private _effectiveVolume(channelVol: number): number {
    return channelVol * this._masterVolume;
  }

  private _play(channel: string, asset: string, loop?: boolean, fadeIn?: number) {
    if (!this.unlocked) {
      this.pendingCommands.push(() => this._play(channel, asset, loop, fadeIn));
      return;
    }

    if (!this.channels.has(channel)) {
      this.channels.set(channel, { sound: null, asset: null, volume: 1.0, loop: false });
    }

    this._ensureLoaded(asset, () => {
      const ch = this.channels.get(channel)!;
      const shouldLoop = loop ?? false;
      const effectiveVol = this._effectiveVolume(ch.volume);

      if (channel === "bgm" && ch.sound && ch.sound.isPlaying) {
        const oldSound = ch.sound;
        this.scene.tweens.add({
          targets: oldSound,
          volume: 0,
          duration: BGM_CROSSFADE_MS,
          ease: "Sine.easeIn",
          onComplete: () => {
            oldSound.stop();
            oldSound.destroy();
          },
        });
      } else if (ch.sound) {
        ch.sound.stop();
        ch.sound.destroy();
      }

      const newSound = this.scene.sound.add(asset, {
        volume: fadeIn ? 0 : effectiveVol,
        loop: shouldLoop,
      }) as PlayableSound;
      newSound.play();

      if (fadeIn) {
        this.scene.tweens.add({
          targets: newSound,
          volume: effectiveVol,
          duration: fadeIn * 1000,
          ease: "Sine.easeOut",
        });
      }

      ch.sound = newSound;
      ch.asset = asset;
      ch.loop = shouldLoop;
    });
  }

  private _stop(channel: string, fadeOut?: number) {
    const ch = this.channels.get(channel);
    if (!ch?.sound) return;

    if (fadeOut && fadeOut > 0) {
      const sound = ch.sound;
      ch.sound = null;
      ch.asset = null;
      ch.loop = false;
      this.scene.tweens.add({
        targets: sound,
        volume: 0,
        duration: fadeOut * 1000,
        ease: "Sine.easeIn",
        onComplete: () => {
          sound.stop();
          sound.destroy();
        },
      });
    } else {
      ch.sound.stop();
      ch.sound.destroy();
      ch.sound = null;
      ch.asset = null;
      ch.loop = false;
    }
  }

  setChannelVolume(channel: string, volume: number) {
    const ch = this.channels.get(channel);
    if (!ch) return;
    ch.volume = Math.max(0, Math.min(1, volume));
    if (ch.sound) {
      ch.sound.volume = this._effectiveVolume(ch.volume);
    }
  }

  setMasterVolume(volume: number) {
    this._masterVolume = Math.max(0, Math.min(1, volume));
    for (const ch of this.channels.values()) {
      if (ch.sound) {
        ch.sound.volume = this._effectiveVolume(ch.volume);
      }
    }
  }

  getMasterVolume(): number {
    return this._masterVolume;
  }

  getChannelVolume(channel: string): number {
    return this.channels.get(channel)?.volume ?? 1.0;
  }

  getSnapshot(): AudioSnapshot {
    const channels: AudioSnapshot["channels"] = {};
    for (const [name, ch] of this.channels) {
      channels[name] = {
        asset: ch.asset,
        volume: ch.volume,
        loop: ch.loop,
        position: ch.sound ? ch.sound.seek : 0,
      };
    }
    return { channels, masterVolume: this._masterVolume };
  }

  restoreSnapshot(snapshot: AudioSnapshot) {
    this._masterVolume = snapshot.masterVolume;

    for (const [name, state] of Object.entries(snapshot.channels)) {
      const ch = this.channels.get(name);
      if (!ch) continue;

      ch.volume = state.volume;

      if (ch.asset !== state.asset) {
        if (ch.sound) {
          ch.sound.stop();
          ch.sound.destroy();
          ch.sound = null;
          ch.asset = null;
          ch.loop = false;
        }
        if (state.asset) {
          this._play(name, state.asset, state.loop);
        }
      }
    }
  }

  stopAll() {
    for (const ch of this.channels.values()) {
      if (ch.sound) {
        ch.sound.stop();
        ch.sound.destroy();
        ch.sound = null;
        ch.asset = null;
      }
    }
    this.pendingCommands = [];
    this.loadQueue.clear();
  }

  shutdown() {
    for (const unsub of this.unsubs) unsub();
    this.unsubs = [];

    for (const ch of this.channels.values()) {
      if (ch.sound) {
        ch.sound.stop();
        ch.sound.destroy();
        ch.sound = null;
        ch.asset = null;
      }
    }

    this.pendingCommands = [];
    this.loadQueue.clear();
  }
}
